import { injectable, inject } from "tsyringe";
import { makeAutoObservable, runInAction } from "mobx";
import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import { TOKEN_IWrapTime, type IWrapTime } from "@/services/WrapTime";
import type { ProjectUser } from "@/ProjectWorld";

import RunningActionStore, { TOKEN_RunningActionStore } from "../utility-stores/RunningActionStore";
import { numberToRgbaString } from "@/lib/utils";
import { AccessLevel } from "@utilitydelta/eventplanedb-client";
import { TOKEN_IProjectEventGenerator, type IProjectEventGenerator } from "@/services/ProjectEventGenerator";

@injectable()
export default class UserStore {
    private _projectId: string | undefined;
    private _isOAuthUser: boolean = false;
    private _id: string = '';
    private _isMe: boolean = false;
    public get isMe(): boolean {
        return this._isMe;
    }
    public set isMe(value: boolean) {
        this._isMe = value;
    }
    private _randomUsername?: string;
    private _lastSeenOnTimestamp: number = 0;
    public get lastSeenOnTimestamp(): number {
        return this._lastSeenOnTimestamp;
    }
    private _lastSeenOn: string | undefined = undefined;
    private _accessLevel: AccessLevel = AccessLevel.None;
    private _lastColor: number = 0;

    constructor(
        @inject(TOKEN_IWrapTranslations) private t: IWrapTranslations,
        @inject(TOKEN_IWrapTime) private wrapTime: IWrapTime,
        @inject(TOKEN_IProjectEventGenerator) private projectEventGenerator: IProjectEventGenerator,
        @inject(TOKEN_RunningActionStore) private runningActionStore: RunningActionStore,
    ) {
        makeAutoObservable(this);

        setInterval(() => {
            runInAction(() => {
                this._lastSeenOn = this._lastSeenOnTimestamp === 0 ? undefined : this.wrapTime.fromUnixTimestampToRelativeString(this._lastSeenOnTimestamp);
            });
        }, 30000);
    }

    init = (user: ProjectUser) => {
        this._id = user.id;
        this._projectId = user.pi;
        this._randomUsername = user.randomUsername;
        this._lastSeenOnTimestamp = user.lastSeenOn;
        this._isOAuthUser = user.isOAuthUser;
        this._lastSeenOn = this._lastSeenOnTimestamp === 0 ? undefined : this.wrapTime.fromUnixTimestampToRelativeString(this._lastSeenOnTimestamp);
        this._accessLevel = user.accessLevel;
        this._lastColor = user.color;
    }

    public get id(): string {
        return this._id;
    }

    set lastColor(value: number) {
        this._lastColor = value;
    }
    get lastColor(): string {
        return numberToRgbaString(this._lastColor);
    }
    get username(): string {
        return this._randomUsername || this._id;
    }

    get isOnline(): boolean {
        return false;
    }

    get accessLevelDisplay(): string {
        return this._accessLevel !== AccessLevel.None ? this.t.t(`access.${this._accessLevel}`) : this.t.t("no_access");
    }

    get lastSeen(): string | undefined {
        return this._lastSeenOn;
    }

    disableUser = async () => {
        if (!confirm(this.t.t('confirm_disabling_user', { username: this.username }))) return;
        await this.runningActionStore.manageAction(async () => {
            await this.projectEventGenerator.userOrClientDisabled(this._projectId!, this._id, this._isOAuthUser);
        }, true, this.t.t('disabling_user', { username: this.username }));
    }

    set randomUsername(value: string | undefined) {
        this._randomUsername = value;
    }

    set lastSeenOn(value: number) {
        this._lastSeenOnTimestamp = value;
        this._lastSeenOn = this._lastSeenOnTimestamp === 0 ? undefined : this.wrapTime.fromUnixTimestampToRelativeString(this._lastSeenOnTimestamp);
    }

    set accessLevel(value: AccessLevel) {
        this._accessLevel = value;
    }

    get isOwner() {
        return this._accessLevel === AccessLevel.Owner;
    }

    get noAccess() {
        return this._accessLevel === AccessLevel.None;
    }
}